import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, HelpCircle, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { buildNav } from "../lib/nav";
import { initials } from "../lib/format";
import NotificationBell from "./NotificationBell";
import PoweredBy from "./PoweredBy";
import { EnvSwitch, EnvBanner } from "./EnvSwitch";
import api from "../lib/api";

const ROLE_LABEL = {
  manager: "Manager", admin: "Reception / Admin", technician: "Technician",
  technical_manager: "Technical Manager",
  parts: "Parts / Spares", dispatch: "Dispatch", customer: "Customer Portal",
};

// SPT-PTE-2026-0059: RFQ Inbox nav bubble must count OPEN RFQ JOBS (one row
// per request — matches the page's own "Open" tab: submitted/reviewed/quoted),
// never equipment item counts.
const RFQ_OPEN_STATUSES = new Set(["submitted", "reviewed", "quoted"]);
const RFQ_POLL_MS = 30000;

export default function AppShell() {
  const { user, perms, logout } = useAuth();
  const navigate = useNavigate();
  const isCust = user?.user_type === "customer";
  const groups = buildNav(perms || {}, isCust);
  const canViewSops = !isCust && perms?.sops?.view;
  const canEditSops = !isCust && perms?.sops?.edit;
  const canViewRfqs = !isCust && !!perms?.rfqs?.view;
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem("pte_sb_collapsed") === "1"; } catch { return false; }
  });
  const [rfqOpenCount, setRfqOpenCount] = React.useState(0);

  React.useEffect(() => {
    if (!canViewRfqs) return undefined;
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/rfqs");
        const rows = Array.isArray(data) ? data : [];
        if (alive) setRfqOpenCount(rows.filter((r) => RFQ_OPEN_STATUSES.has(r.status)).length);
      } catch { /* silent — badge just stays at its last known value */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, RFQ_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [canViewRfqs]);

  const toggleSidebar = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("pte_sb_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const roleLabel = isCust ? "Customer Portal" : ROLE_LABEL[user?.role] || "Staff";

  return (
    <div className={`app ${isCust ? "cust" : ""} ${collapsed ? "sb-collapsed" : ""}`}>
      <aside className="sidebar" data-testid="sidebar" data-collapsed={collapsed ? "1" : "0"}>
        <div className="sb-brand">
          {!collapsed && <Logo scale={0.95} dark={!isCust} />}
          {collapsed && <Logo scale={0.7} dark={!isCust} compact />}
        </div>
        <nav className="sb-nav" data-testid="sidebar-nav">
          {groups.map((g) => (
            <div className="nav-group" key={g.label}>
              {!collapsed && <div className="nav-label">{g.label}</div>}
              {g.items.map((it) => (
                <NavLink
                  key={it.path}
                  to={it.path}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  data-testid={`nav-${it.path.split("/").pop()}`}
                  title={collapsed ? it.label : undefined}
                >
                  <it.icon size={18} />
                  {!collapsed && <span>{it.label}</span>}
                  {it.module === "rfqs" && !collapsed && rfqOpenCount > 0 && (
                    <span className="badge-n" data-testid="nav-rfqs-badge">
                      {rfqOpenCount > 99 ? "99+" : rfqOpenCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sb-foot">
          <div className="sb-user">
            <div className="avatar">{initials(user?.name)}</div>
            {!collapsed && (
              <div style={{ minWidth: 0 }}>
                <div data-name style={{ fontWeight: 700, fontSize: 13, color: isCust ? "var(--text)" : "#f0eee6" }}>{user?.name || user?.email}</div>
                <div style={{ fontSize: 11, color: isCust ? "var(--text-3)" : "#86857d" }}>{roleLabel}</div>
              </div>
            )}
            <button className="icon-btn" style={{ marginLeft: "auto", color: isCust ? "var(--text-2)" : "#86857d" }} onClick={logout} title="Sign out" data-testid="logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            className="icon-btn"
            onClick={toggleSidebar}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            data-testid="sidebar-toggle"
            aria-label="Toggle sidebar"
          >
            <Menu size={18} />
          </button>
          <div style={{ flex: 1 }} />
          <EnvSwitch />
          <span className="badge b-gold" style={{ marginRight: 4 }}><span className="bd" />{roleLabel}</span>
          <NotificationBell dark={!isCust} />
          {canViewSops && (
            <button className="icon-btn" title={canEditSops ? "Help / Learn (SOP / Training)" : "Help / Learn — SOPs & training guides"}
              onClick={() => navigate("/app/sops")} data-testid="help-learn-btn">
              <HelpCircle size={18} />
            </button>
          )}
          {!canViewSops && (
            <button className="icon-btn" title="Help"><HelpCircle size={18} /></button>
          )}
        </header>
        <EnvBanner />
        <main className="content">
          <div className="fade-in" key={window.location.pathname}>
            <Outlet />
          </div>
        </main>
      </div>
      <PoweredBy />
    </div>
  );
}
